import { useMemo, useState } from 'react'
import type { SessionCommand } from '@fitting-room/contracts'
import { StepShell } from '../../components/RoomChrome'
import { formatFacetValue } from '../../lib/format'
import { uiStrings } from '../../lib/strings'
import { text } from '../../lib/text'
import { useRankedGarments } from '../useSession'
import type { StepProps } from '../types'
import { usePendingExit } from '../usePendingExit'

/**
 * A histogram with two handles. The bars are the tenant's own facet data, so
 * the customer can see where the catalogue actually sits rather than guessing
 * against an empty axis, and the count under the axis updates as it moves.
 *
 * The count is derived from the histogram itself (overlap-weighted) rather
 * than round-tripping on every pixel of drag — it is a live sense of "how much
 * is left", and the authoritative number arrives with the deck.
 */
export function RangeFilterStep({ step, ctx }: StepProps<'range_filter'>) {
  const { view, locale, token } = ctx
  const t = uiStrings(locale)

  // Warms the deck for the next step. The currency comes with the facet, not
  // from the deck: the customer's garments carry no price to read it off.
  useRankedGarments(token, view)

  const facet = view.rangeFacets[step.attributeKey]
  const currency = facet?.currency
  const definition = view.tenant.attributes.find((a) => a.key === step.attributeKey)
  const unit = definition?.unit

  const saved = view.session.answers.ranges[step.attributeKey]
  const [lo, setLo] = useState(() => saved?.min ?? facet?.min ?? 0)
  const [hi, setHi] = useState(() => saved?.max ?? facet?.max ?? 0)
  // The handles open at the facet's own bounds when she has said nothing, so
  // "she moved one" is the only thing that makes this a range of hers.
  const [moved, setMoved] = useState(false)

  const stepSize = useMemo(() => {
    if (!facet) return 1
    const span = facet.max - facet.min
    if (span <= 0) return 1
    return Math.max(1, Math.round(span / 100))
  }, [facet])

  const peak = useMemo(() => (facet ? Math.max(1, ...facet.buckets.map((b) => b.count)) : 1), [facet])

  const remaining = useMemo(() => {
    if (!facet) return 0
    let total = 0
    for (const bucket of facet.buckets) {
      const width = bucket.to - bucket.from
      if (width <= 0) {
        if (bucket.from >= lo && bucket.from <= hi) total += bucket.count
        continue
      }
      const overlap = Math.max(0, Math.min(bucket.to, hi) - Math.max(bucket.from, lo))
      total += (overlap / width) * bucket.count
    }
    return Math.round(total)
  }, [facet, lo, hi])

  const format = (value: number) => formatFacetValue(step.attributeKey, value, locale, { ...(currency ? { currency } : {}), ...(unit ? { unit } : {}) })

  const untouched = !facet || (lo <= facet.min && hi >= facet.max)
  const command: SessionCommand = {
    type: 'set_range',
    attributeKey: step.attributeKey,
    range: untouched ? null : { min: lo, max: hi },
  }

  usePendingExit(ctx, moved ? () => [command] : null)

  const label = text(step.title, locale) || (definition ? text(definition.label, locale) : step.attributeKey)

  return (
    <StepShell
      title={label}
      body={text(step.body, locale)}
      progress={ctx.progress}
      locale={locale}
      skip={
        step.skippable
          ? {
              label: t.skipThis,
              disabled: ctx.pending,
              onSkip: () => ctx.advance([{ type: 'set_range', attributeKey: step.attributeKey, range: null }]),
            }
          : undefined
      }
      footer={
        <div className="pb-2" style={{ background: 'linear-gradient(to top, var(--fr-ground-deep) 62%, transparent)' }}>
          <button type="button" className="fr-btn fr-btn-primary w-full" disabled={ctx.pending} onClick={() => ctx.advance([command])}>
            {t.continue}
          </button>
        </div>
      }
    >
      {!facet ? (
        <p className="text-sm" style={{ color: 'var(--fr-ink-dim)' }}>
          {t.anyAmount}
        </p>
      ) : (
        <div>
          <div className="mb-6 flex items-baseline justify-between">
            <span className="fr-display text-[1.5rem]">{untouched ? t.anyAmount : `${format(lo)} – ${format(hi)}`}</span>
            {!untouched ? (
              <button
                type="button"
                className="fr-btn-quiet"
                onClick={() => {
                  setMoved(true)
                  setLo(facet.min)
                  setHi(facet.max)
                }}
              >
                {t.clear}
              </button>
            ) : null}
          </div>

          {step.presentation === 'histogram' ? (
            <div className="flex h-28 items-end gap-[2px]" aria-hidden="true">
              {facet.buckets.map((bucket, index) => {
                const inRange = bucket.to > lo && bucket.from < hi
                return (
                  <div
                    key={`${bucket.from}-${bucket.to}-${index}`}
                    className="flex-1 transition-[background-color,height] duration-200"
                    style={{
                      height: `${Math.max(3, (bucket.count / peak) * 100)}%`,
                      background: inRange ? 'var(--fr-primary)' : 'var(--fr-rule)',
                      opacity: inRange ? 0.9 : 0.55,
                    }}
                  />
                )
              })}
            </div>
          ) : null}

          <div className="relative mt-3 h-10">
            <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2" style={{ background: 'var(--fr-rule)' }} />
            <div
              className="absolute top-1/2 h-px -translate-y-1/2"
              style={{
                background: 'var(--fr-primary)',
                left: `${((lo - facet.min) / Math.max(1, facet.max - facet.min)) * 100}%`,
                right: `${100 - ((hi - facet.min) / Math.max(1, facet.max - facet.min)) * 100}%`,
              }}
            />
            <input
              className="fr-range"
              type="range"
              min={facet.min}
              max={facet.max}
              step={stepSize}
              value={lo}
              aria-label={`${label} — minimum`}
              aria-valuetext={format(lo)}
              onChange={(event) => {
                setMoved(true)
                setLo(Math.min(Number(event.target.value), hi))
              }}
            />
            <input
              className="fr-range"
              type="range"
              min={facet.min}
              max={facet.max}
              step={stepSize}
              value={hi}
              aria-label={`${label} — maximum`}
              aria-valuetext={format(hi)}
              onChange={(event) => {
                setMoved(true)
                setHi(Math.max(Number(event.target.value), lo))
              }}
            />
          </div>

          <div className="mt-1 flex justify-between">
            <span className="fr-eyebrow">{format(facet.min)}</span>
            <span className="fr-eyebrow">{format(facet.max)}</span>
          </div>

          <p className="mt-8 text-sm" aria-live="polite" style={{ color: 'var(--fr-ink-dim)' }}>
            {t.inRange(remaining)}
          </p>
        </div>
      )}
    </StepShell>
  )
}
